import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [logoMessage, setLogoMessage] = useState('FBAJANS İLE KOLAY E-TİCARET'); // Logo mesajı state'i
  const [logoFile, setLogoFile] = useState(null); // Logo dosyası state'i
  const [logoLoading, setLogoLoading] = useState(true); // Logo yükleme durumu
  const [pendingApplicationsCount, setPendingApplicationsCount] = useState(0); // Bekleyen başvuru sayısı

  const menuItems = [
    { path: '/admin/dashboard', icon: 'fas fa-home', label: 'Admin Panel' },
    { path: '/admin/firms', icon: 'fas fa-building', label: 'Firmalar' },
    { path: '/admin/applications', icon: 'fas fa-clipboard-list', label: 'Başvurular', count: pendingApplicationsCount },
    { path: '/admin/integrations', icon: 'fas fa-plug', label: 'Entegrasyonlar' },
    { path: '/admin/message-pool', icon: 'fas fa-inbox', label: 'Message Pool' },
    { path: '/admin/users', icon: 'fas fa-users', label: 'Kullanıcılar' },
    { path: '/admin/logs', icon: 'fas fa-file-alt', label: 'Loglar' },
    { path: '/admin/settings', icon: 'fas fa-cog', label: 'Ayarlar' }
  ];

  // Logo mesajını ve dosyasını backend'den çek
  useEffect(() => {
    const fetchLogoMessage = async () => {
      setLogoLoading(true);
      try {
        const response = await fetch('/api/admin/settings');
        const data = await response.json();
        if (data.success && data.data.logo_message) {
          setLogoMessage(data.data.logo_message);
        }
        if (data.success && data.data.logo_file) {
          setLogoFile(data.data.logo_file);
        }
      } catch (error) {
        console.error('Logo mesajı çekilemedi:', error);
      } finally {
        setLogoLoading(false);
      }
    };

    fetchLogoMessage();
  }, []);

  // Bekleyen başvuru sayısını çek
  useEffect(() => {
    const fetchPendingApplicationsCount = async () => {
      try {
        const response = await fetch('/api/admin/applications');
        const data = await response.json();
        if (data.success) {
          const pendingCount = (data.data || []).filter(app => app.status === 'pending').length;
          setPendingApplicationsCount(pendingCount);
        }
      } catch (error) {
        console.error('Başvuru sayısı çekilemedi:', error);
      }
    };

    fetchPendingApplicationsCount();
    
    // Her 24 saatte bir güncelle (24 * 60 * 60 * 1000 = 86400000 ms)
    const interval = setInterval(fetchPendingApplicationsCount, 86400000);
    return () => clearInterval(interval);
  }, []);

  const MOBILE_BREAKPOINT = 1200;

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isMobileMenuOpen]);

  const handleLogout = () => {
    // Tüm authentication bilgilerini temizle
    localStorage.clear();
    sessionStorage.clear();
    
    // Ana sayfaya yönlendir
    navigate('/');
  };

  const handleChangePassword = () => {
    navigate('/change-password');
  };

  const handleMenuClick = (path) => {
    navigate(path);
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  // Mobile Sidebar
  const MobileSidebar = () => (
    <>
      {/* Overlay */}
      {isMobileMenuOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1998,
            transition: 'opacity 0.3s ease'
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: isMobileMenuOpen ? 0 : '-280px',
          width: '280px',
          height: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          zIndex: 1999,
          transition: 'left 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '2px 0 10px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Sidebar Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ color: 'white', fontWeight: '700', fontSize: '18px', letterSpacing: '0.5px' }}>
            Menü
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '5px'
            }}
          >
            ×
          </button>
        </div>

        {/* Menu Items */}
        <div style={{ flex: 1, padding: '20px 0', overflowY: 'auto' }}>
          {menuItems.map(item => {
            const isActive = window.location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => handleMenuClick(item.path)}
                style={{
                  width: '100%',
                  padding: '15px 20px',
                  border: 'none',
                  background: isActive ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontSize: '16px',
                  fontWeight: isActive ? '600' : '500',
                  transition: 'all 0.2s ease',
                  textAlign: 'left',
                  borderRadius: '0 25px 25px 0',
                  margin: '2px 0',
                  marginRight: '20px'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.transform = 'translateX(5px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.target.style.background = 'transparent';
                    e.target.style.transform = 'translateX(0)';
                  }
                }}
              >
                <div style={{
                  width: '35px',
                  height: '35px',
                  background: isActive ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className={item.icon} style={{ fontSize: '16px', color: '#ffffff' }}></i>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: '600' }}>
                    {item.label}
                    {item.count !== undefined && (
                      <span style={{
                        marginLeft: '8px',
                        background: item.count > 0 ? '#ff4757' : '#6c757d',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        minWidth: '20px',
                        textAlign: 'center'
                      }}>
                        ({item.count})
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.7)', marginTop: '2px' }}>
                    {item.path === '/admin/dashboard' && 'Sistem durumu'}
                    {item.path === '/admin/firms' && 'Firma yönetimi'}
                    {item.path === '/admin/users' && 'Kullanıcı işlemleri'}
                    {item.path === '/admin/logs' && 'Sistem kayıtları'}
                    {item.path === '/admin/settings' && 'Sistem ayarları'}
                  </div>
                </div>
                {isActive && (
                  <div style={{
                    width: '4px',
                    height: '4px',
                    background: 'white',
                    borderRadius: '50%'
                  }} />
                )}
              </button>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div style={{ padding: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
          <button
            onClick={handleChangePassword}
            style={{
              width: '100%',
              padding: '15px',
              border: 'none',
              background: 'rgba(255, 255, 255, 0.15)',
              color: 'white',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '12px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.25)';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.15)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            <div style={{
              width: '30px',
              height: '30px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="fas fa-key" style={{ color: '#ffffff' }}></i>
            </div>
            <span>Şifre Değiştir</span>
          </button>
          
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '15px',
              border: 'none',
              background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
              color: 'white',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(220, 53, 69, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(220, 53, 69, 0.3)';
            }}
          >
            <div style={{
              width: '30px',
              height: '30px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="fas fa-sign-out-alt" style={{ color: '#ffffff' }}></i>
            </div>
            <span>Çıkış Yap</span>
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      overflowX: 'hidden'
    }}>

      {/* Mobile Sidebar */}
      {isMobile && <MobileSidebar />}

      {/* Header */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '15px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Mobile Menu Button & Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          {isMobile && (
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '5px',
                color: '#333'
              }}
            >
              ☰
            </button>
          )}
          
                     <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
             <img 
               src={logoFile ? `/api/logo/${logoFile}` : "/logo.png"} 
               alt="FBAJANS Logo" 
               style={{ height: '40px', width: 'auto' }} 
             />
           </div>
        </div>

        {/* Desktop Menu Items */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {menuItems.map(item => {
              const isActive = window.location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  style={{
                    padding: '10px 16px',
                    border: 'none',
                    background: isActive ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                    color: isActive ? 'white' : '#666',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px',
                    fontWeight: isActive ? '600' : '500',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.target.style.background = 'rgba(102, 126, 234, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.target.style.background = 'transparent';
                    }
                  }}
                >
                  <i className={item.icon} style={{ fontSize: '14px', marginRight: '8px' }}></i>
                  <span>
                    {item.label}
                    {item.count !== undefined && (
                      <span style={{
                        marginLeft: '8px',
                        background: item.count > 0 ? '#ff4757' : '#6c757d',
                        color: 'white',
                        padding: '3px 7px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        minWidth: '18px',
                        textAlign: 'center'
                      }}>
                        ({item.count})
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Action Buttons - Desktop */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={handleChangePassword}
              style={{
                padding: '10px 16px',
                border: 'none',
                background: '#17a2b8',
                color: 'white',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              <i className="fas fa-key" style={{ fontSize: '14px' }}></i>
              <span>Şifre Değiştir</span>
            </button>

            <button
              onClick={handleLogout}
              style={{
                padding: '10px 16px',
                border: 'none',
                background: '#dc3545',
                color: 'white',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              <i className="fas fa-sign-out-alt" style={{ fontSize: '14px' }}></i>
              <span>Çıkış Yap</span>
            </button>
          </div>
        )}

        {/* Mobile Action Button */}
        {isMobile && (
          <button
            onClick={handleLogout}
            style={{
              padding: '8px',
              border: 'none',
              background: '#dc3545',
              color: 'white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <i className="fas fa-sign-out-alt" style={{ color: '#ffffff' }}></i>
          </button>
        )}
      </div>

      {/* Main Content */}
      <div style={{
        padding: isMobile ? '10px' : '20px',
        minHeight: 'calc(100vh - 70px)'
      }}>
        <div style={{
          background: 'white',
          borderRadius: isMobile ? '15px' : '20px',
          minHeight: 'calc(100vh - 110px)',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          {children || <Outlet />}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout; 